import Icon from "./ui/Icon";
import Toggle from "./ui/Toggle";
import DimmerSlider from "./ui/DimmerSlider";
import { BRAND } from "../brand";

const accentColor = BRAND.magenta;

const toneMap = {
  ok: {
    iconBg: "bg-[#0f1b14]",
    iconText: "text-emerald-400",
    subtitle: "text-emerald-400",
  },
  fault: {
    iconBg: "bg-[#2a1313]",
    iconText: "text-red-300",
    subtitle: "text-red-400",
  },
  neutral: {
    iconBg: "bg-brand-navy",
    iconText: "text-brand-light",
    subtitle: "text-brand-light/70",
  },
};

export default function PdmCard({ device, onToggle, onDimChange, onCardPress }) {
  const tone = toneMap[device.status] || toneMap.neutral;
  const mode = device.mode || (device.dimmable ? "Dimming" : "Switching");
  const isDirect = mode === "Direct";
  const isDimming = mode === "Dimming";
  const control = device.control || {};
  const hasFault = device.faults?.length > 0 || device.status === "fault";

  const handleCardClick = () => {
    if (onCardPress) onCardPress(device.id);
  };

  return (
    <div
      className={`w-full rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 space-y-3 shadow-sm ${
        onCardPress ? "hover:border-brand-magenta/70 transition cursor-pointer" : ""
      }`}
      onClick={handleCardClick}
      role={onCardPress ? "button" : undefined}
      tabIndex={onCardPress ? 0 : -1}
      onKeyDown={(e) => {
        if (onCardPress && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onCardPress(device.id);
        }
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-11 w-11 items-center justify-center rounded-full text-xl ${tone.iconBg} ${tone.iconText}`}
          >
            <Icon name={device.icon || "lightbulb"} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-brand-light">{device.name}</span>
            {hasFault && (
              <span className="text-xs font-semibold text-red-400">Fault</span>
            )}
          </div>
        </div>
        {isDirect ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (!device.disabled) onToggle?.(device.id, true);
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              if (!device.disabled) onToggle?.(device.id, false);
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              if (!device.disabled) onToggle?.(device.id, false);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              if (!device.disabled) onToggle?.(device.id, true);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (!device.disabled) onToggle?.(device.id, false);
            }}
            disabled={device.disabled}
            className={`px-4 py-2 rounded-xl text-sm font-semibold ${
              device.disabled
                ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                : "bg-brand-magenta text-white active:scale-[0.98]"
            }`}
          >
            Pulse
          </button>
        ) : (
          <Toggle
            on={Boolean(control.on)}
            disabled={device.disabled}
            onChange={(next) => onToggle?.(device.id, next)}
            suppressPropagation
          />
        )}
      </div>

      {isDimming && (
        <div className="flex items-center gap-3">
          <Icon name="brightness_6" className="text-lg" style={{ color: accentColor }} />
          <DimmerSlider
            value={control.dim ?? 0}
            onChange={(value) => onDimChange?.(device.id, value)}
            suppressPropagation
            className="flex-1"
          />
          <span className="text-xs font-semibold text-brand-light">{control.dim ?? 0}%</span>
        </div>
      )}

      {hasFault && (
        <div className="rounded-2xl border border-red-500/70 bg-[#2a0f12] px-3 py-2 text-sm font-semibold text-red-100">
          {device.faults?.join(", ") || "Fault detected"}
        </div>
      )}
    </div>
  );
}
